import PageBreadcrumb from 'components/common/PageBreadcrumb';
import React, { useEffect } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import {
  fetchNotifications,
  selectNotifications,
  selectNotificationsLoading
} from 'store/notificationsSlice';
import PhoenixLoader from 'components/common/PhoenixLoader';
import { Badge } from 'react-bootstrap';

const Notification = () => {
  const dispatch = useDispatch<AppDispatch>();
  const apiNotifications = useSelector(selectNotifications);
  const loading = useSelector(selectNotificationsLoading);

  // Define breadcrumb items
  const notificationsBreadcrumbItems = [
    {
      label: 'Pages',
      url: '#!'
    },
    {
      label: 'Notifications',
      active: true
    }
  ];

  useEffect(() => {
    dispatch(fetchNotifications(false)); // Fetch all notifications
  }, [dispatch]);

  // Get badge color based on notification type
  const getTypeBadge = (type: string) => {
    const typeColors: Record<string, string> = {
      info: 'info',
      success: 'success',
      warning: 'warning',
      error: 'danger'
    };
    return (
      <Badge bg={typeColors[type] || 'secondary'} className="fs-9">
        {type.toUpperCase()}
      </Badge>
    );
  };

  // Format time display
  const formatTime = (dateString: string): string => {
    // Ensure proper UTC parsing - add 'Z' if not present
    const utcString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    const date = new Date(utcString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  // Group notifications by date
  const groupNotificationsByDate = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const todayNotifications = apiNotifications.filter(notification => {
      const notificationDate = new Date(notification.created_at);
      return notificationDate.toDateString() === today.toDateString();
    });

    const yesterdayNotifications = apiNotifications.filter(notification => {
      const notificationDate = new Date(notification.created_at);
      return notificationDate.toDateString() === yesterday.toDateString();
    });

    const olderNotifications = apiNotifications.filter(notification => {
      const notificationDate = new Date(notification.created_at);
      return (
        notificationDate.toDateString() !== today.toDateString() &&
        notificationDate.toDateString() !== yesterday.toDateString()
      );
    });

    return { todayNotifications, yesterdayNotifications, olderNotifications };
  };

  const { todayNotifications, yesterdayNotifications, olderNotifications } =
    groupNotificationsByDate();

  if (loading) {
    return <PhoenixLoader />;
  }

  return (
    <div>
      <PageBreadcrumb items={notificationsBreadcrumbItems} />
      <div className="d-flex justify-content-between align-items-center mb-5">
        <h2 className="mb-0">Notifications</h2>
        <div className="text-body-tertiary fs-9">
          {apiNotifications.length} total notifications
        </div>
      </div>

      {todayNotifications.length > 0 && (
        <>
          <h5 className="text-body-emphasis mb-3">Today</h5>
          <div className="mx-n4 mx-lg-n6 mb-5 border-top">
            {todayNotifications.map((notification, index) => (
              <div
                key={notification.id}
                className={classNames(
                  'p-3 border-bottom border-translucent notification-item',
                  {
                    'bg-light': !notification.is_read,
                    'border-bottom-0': index === todayNotifications.length - 1
                  }
                )}
              >
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="mb-0 text-body-emphasis">
                    {notification.title}
                  </h6>
                  {getTypeBadge(notification.type)}
                </div>
                <p className="text-body mb-2 fs-9">{notification.message}</p>
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-body-tertiary fs-9">
                    {notification.related_entity_type && (
                      <>• {notification.related_entity_type}</>
                    )}
                  </small>
                  <small className="text-body-tertiary fs-9">
                    {formatTime(notification.created_at)}
                  </small>
                </div>
                {!notification.is_read && (
                  <div className="mt-2">
                    <Badge bg="primary" className="fs-10">
                      NEW
                    </Badge>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {yesterdayNotifications.length > 0 && (
        <>
          <h5 className="text-body-emphasis mb-3">Yesterday</h5>
          <div className="mx-n4 mx-lg-n6 mb-5 border-top">
            {yesterdayNotifications.map((notification, index) => (
              <div
                key={notification.id}
                className={classNames('p-3 border-bottom border-translucent', {
                  'bg-light': !notification.is_read,
                  'border-bottom-0': index === yesterdayNotifications.length - 1
                })}
              >
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="mb-0 text-body-emphasis">
                    {notification.title}
                  </h6>
                  {getTypeBadge(notification.type)}
                </div>
                <p className="text-body mb-2 fs-9">{notification.message}</p>
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-body-tertiary fs-9">
                    {notification.related_entity_type && (
                      <>• {notification.related_entity_type}</>
                    )}
                  </small>
                  <small className="text-body-tertiary fs-9">
                    {formatTime(notification.created_at)}
                  </small>
                </div>
                {!notification.is_read && (
                  <div className="mt-2">
                    <Badge bg="primary" className="fs-10">
                      NEW
                    </Badge>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {olderNotifications.length > 0 && (
        <>
          <h5 className="text-body-emphasis mb-3">Older</h5>
          <div className="mx-n4 mx-lg-n6 mb-9 border-top">
            {olderNotifications.map((notification, index) => (
              <div
                key={notification.id}
                className={classNames('p-3 border-bottom border-translucent', {
                  'bg-light': !notification.is_read,
                  'border-bottom-0': index === olderNotifications.length - 1
                })}
              >
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="mb-0 text-body-emphasis">
                    {notification.title}
                  </h6>
                  {getTypeBadge(notification.type)}
                </div>
                <p className="text-body mb-2 fs-9">{notification.message}</p>
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-body-tertiary fs-9">
                    {notification.related_entity_type && (
                      <>• {notification.related_entity_type}</>
                    )}
                  </small>
                  <small className="text-body-tertiary fs-9">
                    {formatTime(notification.created_at)}
                  </small>
                </div>
                {!notification.is_read && (
                  <div className="mt-2">
                    <Badge bg="primary" className="fs-10">
                      NEW
                    </Badge>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {apiNotifications.length === 0 && (
        <div className="text-center p-5">
          <h5>No notifications</h5>
          <p className="text-muted">You don't have any notifications yet.</p>
        </div>
      )}
    </div>
  );
};

export default Notification;
